import { AxiosError, AxiosResponse } from 'axios';

import { IPagination } from '..';
import { CountryName } from '../../../config';

export interface IDeletedCustomer {
  callerId: number;
  customGroupIds: string;
  customerId: number;
  customerName: string;
  /** Queue list responses may expose `name` / `id` for table display and routing */
  name?: string;
  id?: number;
  deleteBy?: string;
  status?: number;
  deletedAt?: string;
  districtGroupIds: string;
  domainId: number;
  receivedAt: string;
  sectionGroupIds: string;
  unitGroupIds: string;
  validationErrors: null;
}

export interface IRemovableCustomer {
  id: number;
  name: string;
}

export interface ICustomerError extends AxiosError {
  response: AxiosResponse<{
    additionalErrorParameters?: { exceptionMessage?: string | null } | null;
    field?: string;
    message: string;
    severity: string;
  }>;
}

type ProgressStatus = 'not started yet' | 'in progress' | 'done' | 'failed';

export interface ICreateCustomerProgress {
  id: string;
  domainName: string;
  domainId: number;
  currentStatus: ProgressStatus;
  receivedAt: string;
  messages: string[];
  traceId?: string;
}

export interface ICustomerBasicInfo {
  name: string;
  companyAlias?: string;
  locale: string;
  country: CountryName;
  timeZone: string;
  salesForceId: string;
  dateCreated: string;
  validUpto: string;
  useDistricts: boolean;
  localShiftTypes: boolean;
  shareWithinDistrict: boolean;
  active: boolean;
}

export type ICustomer = ICustomerBasicInfo & {
  customerId: number;
  rootStoreId: number;
  email: string;
  deleteBy?: string;
};

export type ICreateCustomerRequest = ICustomerBasicInfo & {
  sourceDomainId?: number;
  entityTypes?: string[];
  /** The environment the source tenant is in, for a copy into another environment. */
  sourceEnvironment?: string;
};

/**
 * A system admin token is only valid in the environment that issued it, so a copy carries one for
 * each end: the source token reads the tenant being copied, the target token writes the new one.
 * They are the same token when both ends are the same environment. Sent as headers - a credential
 * does not belong in a request body.
 */
export interface ISysAdminTokens {
  source: string;
  target: string;
}

export type ICustomerUpdatePayload = ICustomerBasicInfo &
  Pick<ICustomer, 'customerId' | 'rootStoreId' | 'email'>;

export interface ICustomersForDeletion {
  id: number;
  deleteBy: string;
}

export interface ICustomerPaginationContent extends Pick<
  ICustomer,
  'name' | 'active' | 'email' | 'dateCreated'
> {
  domainId: number;
}

export type ICustomerPagination = IPagination<ICustomerPaginationContent>;

export interface ILicense {
  licenseBasedOn: 'units' | 'users';
  numberOfLicenses: number;
  numberOfNonBillableLicenses: number;
  nonBillableLicensesComment: string;
}

export interface ILicenseExtended extends ILicense {
  customerId: number;
  domainId: number;
}

export interface IZoined {
  zoinedCustomerId: string;
  zoinedSecret: string;
}

export interface IZoinedExtended extends IZoined {
  customerId: number;
  zoinedEnabled: boolean;
  domainId: number;
}

export type ScriveLicensePeriodStatus =
  | 'PAST'
  | 'CURRENT'
  | 'FUTURE'
  | 'DELETED';

export interface IScriveLicensePeriod {
  id: string;
  status: ScriveLicensePeriodStatus;
  numLicenses: number;
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  createdBy: string;
  lastEditedAt: string;
  lastEditedBy: string;
  deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export type IScriveLicensePeriodCreatePayload = Pick<
  IScriveLicensePeriod,
  'numLicenses' | 'dateFrom' | 'dateTo'
>;

export interface IConfigPayload {
  identifier: string;
  value: string;
}

export interface IConfig {
  domainId: string;
  payload: IConfigPayload[];
}

export type ConfigType = {
  identifier: string;
  type: string;
  value: boolean;
};

export const MODULE_TITLE_MAP = {
  mobileDeviceModule: 'Mobile devices',
  moduleEngagementBadges: 'Engagement - Badges',
  moduleEngagementPulse: 'Engagement - Pulse',
  webpunchModule: 'Webpunch',
  mobileTAndAModule: 'Quinyx T&A',
  mobileScheduleModule: 'Schedule',
} as const;

export type ModuleName = keyof typeof MODULE_TITLE_MAP;

export interface IModule {
  id?: number;
  groupId: number;
  moduleName: ModuleName;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  enable?: boolean;
}
